const BASE_URL = import.meta.env.VITE_API_URL || '/api';

/** @type {Array<(config: RequestConfig) => RequestConfig>} */
const requestInterceptors = [];

/** @type {Array<(response: Response) => Response>} */
const responseInterceptors = [];

/**
 * @typedef {Object} RequestConfig
 * @property {string} url
 * @property {RequestInit} options
 */

/**
 * Register a request interceptor.
 * @param {(config: RequestConfig) => RequestConfig} fn
 */
export function onRequest(fn) {
  requestInterceptors.push(fn);
}

/**
 * Register a response interceptor.
 * @param {(response: Response) => Response} fn
 */
export function onResponse(fn) {
  responseInterceptors.push(fn);
}

/**
 * Attempt to refresh the access token using the stored refresh token.
 * @returns {Promise<string|null>} The new access token or null.
 */
async function refreshAccessToken() {
  try {
    // Refresh token is sent via httpOnly cookie (credentials: 'include').
    // Fallback to localStorage for backward compatibility during transition.
    const refreshToken = localStorage.getItem('refreshToken');
    const res = await fetch(`${BASE_URL}/auth/refresh`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(refreshToken ? { refreshToken } : {}),
    });

    if (!res.ok) return null;

    const data = await res.json();
    // Backend returns { success, data: { accessToken } }
    const newToken = data.data?.accessToken || data.accessToken || data.token;
    if (!newToken) return null;

    localStorage.setItem('token', newToken);
    return newToken;
  } catch {
    return null;
  }
}

/**
 * Core fetch wrapper with JWT, interceptors, and auto-refresh on 401.
 * @param {string} endpoint - API endpoint (relative to BASE_URL)
 * @param {RequestInit & { params?: Record<string, string> }} options
 * @returns {Promise<any>}
 */
async function request(endpoint, options = {}) {
  const token = localStorage.getItem('token');

  const headers = {
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    ...options.headers,
  };

  let url = `${BASE_URL}${endpoint}`;

  // Append query params if provided
  if (options.params) {
    const searchParams = new URLSearchParams(options.params);
    url += `?${searchParams.toString()}`;
  }

  let config = { ...options, headers, url };

  // Run request interceptors
  for (const interceptor of requestInterceptors) {
    config = interceptor(config);
  }

  const { url: finalUrl, ...fetchOptions } = config;
  delete fetchOptions.params;

  let response = await fetch(finalUrl, fetchOptions);

  // Auto-refresh on 401
  if (response.status === 401 && token) {
    const newToken = await refreshAccessToken();
    if (newToken) {
      headers.Authorization = `Bearer ${newToken}`;
      response = await fetch(finalUrl, { ...fetchOptions, headers });
    } else {
      // Clear auth — ProtectedRoute will handle redirect via React Router
      localStorage.removeItem('token');
      localStorage.removeItem('refreshToken');
      localStorage.removeItem('user');
      throw new ApiError('Session expired', 401, { code: 'SESSION_EXPIRED' });
    }
  }

  // Run response interceptors
  for (const interceptor of responseInterceptors) {
    response = interceptor(response);
  }

  if (!response.ok) {
    const body = await response.json().catch(() => ({ message: response.statusText }));
    const message = body.error?.message || body.message || response.statusText;
    const code = body.error?.code || body.code || null;
    throw new ApiError(message, response.status, { ...body, code });
  }

  // Handle 204 No Content
  if (response.status === 204) return null;

  return response.json();
}

class ApiError extends Error {
  constructor(message, status, data) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.data = data;
  }
}

const api = {
  get: (endpoint, params) => request(endpoint, { method: 'GET', params }),
  post: (endpoint, data) => request(endpoint, { method: 'POST', body: JSON.stringify(data) }),
  put: (endpoint, data) => request(endpoint, { method: 'PUT', body: JSON.stringify(data) }),
  patch: (endpoint, data) => request(endpoint, { method: 'PATCH', body: JSON.stringify(data) }),
  delete: (endpoint) => request(endpoint, { method: 'DELETE' }),
  onRequest,
  onResponse,
};

export default api;
export { ApiError };
